// apps/provider/src/hooks/useCaseBillingAlerts.js
// Case-level billing rule evaluation for visit milestone and documentation trigger badges.

import { useState, useEffect, useMemo } from 'react';
import { apiService } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { useBillingRuleEvaluation } from './useBillingRuleEvaluation';
import { isPocExpired } from '../utils/billingAlertEnrichment';
import { isMedicareCoverage } from '../utils/pnDue';

/**
 * @param {Object} options
 * @param {Object} options.selectedCase - Case object (id, clinical_specialty, status)
 * @param {Array} options.encounters - All encounters for this case
 * @param {Object} options.patient - Patient object (for payer info)
 * @param {string} options.organizationId - Org ID for rule loading
 * @param {{plansOfCare: Array, clinicalNotes: Array}} [options.prefetched] - When the
 *   caller already holds this case's POCs/notes (the chart batches them), pass
 *   them to skip the two fetches here.
 * @returns {{ violations: Array, hasHardBlocks: boolean, hasSoftWarnings: boolean, isLoading: boolean, pocExpired: boolean }}
 *  pocExpired: the case's CURRENT plan of care has already expired (same
 *  isPocExpired helper the calendar alert store uses, so the PN-due predicate
 *  in utils/pnDue.js sees identical inputs on both surfaces).
 */
export function useCaseBillingAlerts({
  selectedCase,
  encounters,
  patient,
  organizationId,
  prefetched,
}) {
  const [plansOfCare, setPlansOfCare] = useState([]);
  const [clinicalNotes, setClinicalNotes] = useState([]);

  useEffect(() => {
    if (prefetched) {
      setPlansOfCare(prefetched.plansOfCare || []);
      setClinicalNotes(prefetched.clinicalNotes || []);
      return;
    }
    if (!selectedCase?.id || !patient?.id) {
      setPlansOfCare([]);
      setClinicalNotes([]);
      return;
    }
    let cancelled = false;

    const fetchData = async () => {
      try {
        const [pocResult, noteResult] = await Promise.all([
          apiService.getPlansOfCare({ case_id: selectedCase.id, patient_id: patient.id }),
          apiService.getClinicalNotes({ case_id: selectedCase.id }),
        ]);
        if (!cancelled) {
          setPlansOfCare(pocResult?.items || pocResult || []);
          setClinicalNotes(noteResult?.items || noteResult || []);
        }
      } catch (err) {
        console.error('Failed to load data for billing alerts:', err);
        if (!cancelled) {
          setPlansOfCare([]);
          setClinicalNotes([]);
        }
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [selectedCase?.id, patient?.id, prefetched]);

  const evaluation = useBillingRuleEvaluation({
    linkedCase: selectedCase,
    caseEncounters: encounters,
    clinicalNotes,
    patient,
    organizationId,
    categoryFilter: ['visit_milestone', 'documentation_trigger'],
    plansOfCare,
  });

  // Org setting: the expired-POC signal applies only to Medicare/MA case
  // coverage (settings.documentation.poc_alerts_medicare_only; utils/pnDue.js)
  const { user } = useUser();
  const pocMedicareOnly = !!user?.organization?.settings?.documentation?.poc_alerts_medicare_only;
  // plan-of-care rows carry the `case` addon (status), which isPocExpired needs
  const pocExpired = useMemo(
    () => (!pocMedicareOnly || isMedicareCoverage(selectedCase, patient)) && isPocExpired(plansOfCare),
    [plansOfCare, pocMedicareOnly, selectedCase, patient]
  );

  return { ...evaluation, pocExpired };
}
